/**
 * Assess the strength of a password or passphrase.
 * Returns { ok, strength, score, details }.
 *
 * Strength bands (default): 0-39 Weak, 40-69 Moderate, 70-100 Strong
 *
 * What makes a passphrase secure?
 * Security comes from length and unpredictability. Using random, unrelated words
 * (rather than common phrases) makes a passphrase much harder for attackers to guess or crack.
 */

/**
 *
 * @param string passWordPhrase
 * @param object opts
 * @returns object          {ok, strength, score, details}
 */
export default function EvaluatePasswordPhrase(
  passWordPhrase,
  opts = {},
  name,
  email
) {
  const MAX_SCORE = 100; // lengthMax(3) + varietyMax(3)

  const thresholds = {
    passphrase: { weakMax: 39, moderateMax: 69 },
    password16: { weakMax: 29, moderateMax: 59 }, // single word, ≥16 chars, mixed classes required
  };

  const cfg = {
    allowedSymbols: "^*%$-!?&@#", //word and phrase
    ppMinAcceptedWords: 4, // phrase = 4
    ppMinAcceptedWordLength: 4, // phrase words length = 4
    pwMinAcceptedWordLength: 16, // pw min length
    pwPolicyBonusPerMetric: 4, // points per met metric
    pwPolicyRequiredCount: 4, // how many must be met for "complete"
    pwPolicyMetrics: ["len16", "lower", "upper", "digit", "symbol"], // which metrics count
    pwPolicyBonusIncludeLen: true, // whether "len16" also earns bonus
    // Strength thresholds
    // weakMax: 39, // word = ?, phrase = 39
    // moderateMax: 69, // word = ?, phrase = 69
    ...opts,
  };

  const symbolClass = escapeForCharClass(cfg.allowedSymbols);
  const allowedCharsRe = new RegExp(`^[A-Za-z0-9 ${symbolClass}]+$`);
  let score = 0; // 0-100
  let complete = false;

  const details = {
    words: [],
    signals: {},
  };

  const messages = {
    policiesMet: [],
    hints: [],
    caution: [],
    errors: [],
    needs: [],
  };

  // --- 0) Basic structure: must be multiple words separated by spaces ---
  if (typeof passWordPhrase !== "string") {
    // "Password or Phrase must be a string."
    return finish("Weak", "danger", 0, messages, null, false);
  }
  const normalized = passWordPhrase.trim().replace(/\s+/g, " ");

  //determine pw or phrase
  const isPassword = !/\s/.test(normalized);

  // config strength thresholds based on pw vs pp
  // strength = score <= weakMax ? "Weak" : score <= moderateMax ? "Moderate" : "Strong";
  if (isPassword) {
    cfg.weakMax = thresholds.password16.weakMax;
    cfg.moderateMax = thresholds.password16.moderateMax;
  } else {
    cfg.weakMax = thresholds.passphrase.weakMax;
    cfg.moderateMax = thresholds.passphrase.moderateMax;
  }

  // Character set sanity (not a hard fail, but we’ll penalize)
  //   if (!allowedCharsRe.test(normalized)) {
  //     details.hints.push("🔴 Remove invalid symbols.");
  //     details.signals.badCharset = true;
  //     //characters outside A–Z, a–z, 0–9, space, or the allowed symbols (^*%$-!?&@#)."
  //   }

  // --- 1) Core metrics ---
  const totalLen = normalized.length;
  const words = normalized.length ? normalized.split(" ") : []; //all words in string
  const lowerWords = words.map((w) => w.toLowerCase());
  const wordCount = words.length; //number of words
  const wordLens = words.map((w) => w.length); //array of each word length
  // const avgWordLen = wordLens.reduce((a, b) => a + b, 0) / wordLens.length;
  details.words = words;

  //passphrase only
  if (!isPassword && words.length < 4) {
    messages.errors.push(
      `🔴 Minimum ${cfg.ppMinAcceptedWords} words, each 4+ characters.`
    );
    // return finish("Weak", "danger", 0, messages, isPassword, false);
  } else if (isPassword && totalLen < cfg.pwMinAcceptedWordLength) {
    messages.errors.push(
      `🔴 Minimum length is ${cfg.pwMinAcceptedWordLength} characters.`
    );

    // return finish("Weak", "danger", 0, messages, isPassword, false);
  }

  // Passphrase only
  // --- 2) Repeats, substrings, similarity ---
  if (!isPassword) {
    let hasDuplicate = false;
    const dupSet = new Set();
    for (const lw of lowerWords) {
      if (dupSet.has(lw)) {
        hasDuplicate = true;
        break;
      }
      dupSet.add(lw);
    }
    if (hasDuplicate) {
      messages.errors.push("🔴 Contains repeated words.");
      details.signals.duplicates = true;
    }

    // Uniqueness (+10 if all unique)
    if (!hasDuplicate) score += 10;

    // Any word a proper substring of another?
    let hasSubstringPair = false;
    for (let i = 0; i < words.length; i++) {
      for (let j = i + 1; j < words.length; j++) {
        const a = words[i];
        const b = words[j];
        if (a.length === b.length) continue;
        if (a.includes(b) || b.includes(a)) {
          // Ignore exact equals (already handled by duplicates)
          if (a.toLowerCase() !== b.toLowerCase()) {
            hasSubstringPair = true;
            break;
          }
        }
      }
      if (hasSubstringPair) break;
    }

    if (hasSubstringPair) {
      // Substring relation penalty
      score -= 8;
      messages.errors.push("🔴 A word cannot be a substring of another word.");
      details.signals.substrings = true;
    }

    // Rough “unrelatedness” test: penalize if many words share ≥3-char common substrings or long common prefixes/suffixes
    let relatedPairs = 0;
    for (let i = 0; i < words.length; i++) {
      for (let j = i + 1; j < words.length; j++) {
        const a = words[i].toLowerCase();
        const b = words[j].toLowerCase();
        const lcs = longestCommonSubstringLen(a, b);
        const commonPref = commonPrefixLen(a, b);
        const commonSuf = commonSuffixLen(a, b);
        if (lcs >= 3 || commonPref >= 3 || commonSuf >= 3) relatedPairs++;
      }
    }

    if (relatedPairs > 0) {
      // Relatedness penalty (up to −12)
      score -= Math.min(12, relatedPairs * 3);
      messages.caution.push(
        "🟠 Avoid related (shared 3+ letter chunks/prefixes/suffixes)."
      );
      details.signals.relatedPairs = relatedPairs;
    }

    if (!hasDuplicate && !hasSubstringPair && relatedPairs === 0)
      messages.policiesMet.push("🟢 Words look unrelated and unique.");
  }

  // --- 3) Pattern checks ---
  const lower = normalized.toLowerCase();

  // score penalties for name/email
  const nameParts = cfg.name.toLowerCase().split(/\s+/).filter(Boolean);
  const emailLocal = cfg.email.toLowerCase().split("@")[0] || "";
  if (
    nameParts.some((p) => p.length >= 3 && lower.includes(p)) ||
    (emailLocal && lower.includes(emailLocal))
  ) {
    score -= 2;
    messages.caution.push("🟠 Avoid using your name or email");
  }

  const bannedPhrases = [
    "correct horse battery staple",
    "hello world",
    "to be or not to be",
    "lorem ipsum",
    "once upon a time",
    "may the force be with you",
    "open sesame",
    "let me in",
    "new york",
    "game of thrones",
    "winter is coming",
  ];
  if (bannedPhrases.some((p) => lower.includes(p))) {
    messages.caution.push("🟠 Avoid common phrases.");
    details.signals.commonPhrase = true;
  }

  const commonWords = new Set([
    // some stopwords + very common password tokens (keep list compact)
    "the",
    "and",
    "to",
    "of",
    "a",
    "in",
    "for",
    "is",
    "on",
    "that",
    "with",
    "as",
    "it",
    "at",
    "this",
    "password",
    "pass",
    "admin",
    "user",
    "qwerty",
    "abc",
    "abcd",
    "abc123",
    "letmein",
    "iloveyou",
    "welcome",
    "retire",
  ]);
  const commonWordHits = lowerWords.filter((w) => commonWords.has(w)).length;
  if (commonWordHits >= Math.ceil(wordCount * 0.5)) {
    messages.caution.push("🟠 Avoid common words and use unrelated words.");
    details.signals.commonWordRatio = commonWordHits / wordCount;
  }

  // Numeric/year & sequential digits
  const digitRuns = normalized.match(/\d{3,}/g) || [];
  let hasSeq = false;
  for (const run of digitRuns) {
    if (isSequential(run) || isSequential(run, true)) {
      hasSeq = true;
      break;
    }
    //includes both 19 and 20 years
    if (/^(19|20)\d{2}$/.test(run)) {
      hasSeq = true;
      break;
    }
    // orig
    // if (/^20\d{2}$/.test(run)) {
    //   hasSeq = true;
    //   break;
    // } // year-like
  }
  if (hasSeq) {
    messages.caution.push("🟠 Avoid numeric sequences or year-like numbers.");
    details.signals.sequentialNumbers = true;
  }

  // Keyboard rows & trivial sequences
  const keyboardish = /(qwerty|asdf|zxcv|1234|9876)/i.test(normalized);
  if (keyboardish) {
    messages.caution.push("🟠 Avoid sequences (e.g., qwerty, 1234).");
    details.signals.keyboardSeq = true;
  }

  // Repeated word blocks like AB AB
  if (hasRepeatedWordBlock(lowerWords)) {
    messages.caution.push(
      "🟠 Avoid repeated word blocks (e.g., EIEI or AB AB)."
    );
    details.signals.repeatedBlocks = true;
  }

  // Month/day names (very common tokens)
  const months =
    /(january|february|march|april|may|june|july|august|september|october|november|december)/i;
  const days = /(monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i;
  if (months.test(lower) || days.test(lower)) {
    messages.caution.push("🟠 Avoid month and/or day names.");
    details.signals.calendarWords = true;
  }

  // --- 4) Character class diversity ---
  const hasUpper = /[A-Z]/.test(normalized);
  const hasLower = /[a-z]/.test(normalized);
  const hasDigit = /\d/.test(normalized);
  const hasAllowedSymbol = new RegExp(`[${symbolClass}]`).test(normalized);
  const hasInvalidSymbol = !allowedCharsRe.test(normalized);

  // update hints
  if (hasLower) messages.policiesMet.push("🟢 Lowercase");
  else if (isPassword) messages.needs.push("⚪️ Lowercase");
  if (hasUpper) messages.policiesMet.push("🟢 Uppercase");
  else if (isPassword) messages.needs.push("⚪️ Uppercase");
  if (hasDigit) messages.policiesMet.push("🟢 Digits");
  else if (isPassword) messages.needs.push("⚪️ Digits");
  if (hasAllowedSymbol) messages.policiesMet.push("🟢 Valid Symbol ^*%$-!?&@#");
  else if (isPassword) messages.needs.push("⚪️ Valid Symbol ^*%$-!?&@#");
  if (hasInvalidSymbol) messages.errors.push("🔴 Invalid symbol not accepted.");
  // Password policy metrics (only used when isPassword)
  const policyMetricChecks = {
    len16: totalLen >= cfg.pwMinAcceptedWordLength,
    lower: hasLower,
    upper: hasUpper,
    digit: hasDigit,
    symbol: hasAllowedSymbol,
  };

  const metricsToUse = cfg.pwPolicyMetrics.filter(
    (m) =>
      m in policyMetricChecks && (m !== "len16" || cfg.pwPolicyBonusIncludeLen)
  );

  const metCount = metricsToUse.reduce(
    (acc, m) => acc + (policyMetricChecks[m] ? 1 : 0),
    0
  );

  // Add per-metric bonus (passwords only)
  if (isPassword) {
    score += metCount * cfg.pwPolicyBonusPerMetric;
  }

  const classCount = [hasUpper, hasLower, hasDigit, hasAllowedSymbol].filter(
    Boolean
  ).length;
  if (classCount < 4)
    messages.hints.push("🟡 Mix upper/lowercase, digits, symbols");

  //   if (classCount >= 3)
  //     details.positives.push("Good variety of character classes.");
  //   else
  //     details.hints.push(
  //       "🔴 Consider mixing upper/lowercase, digits, and allowed symbols for more complexity."
  //     );

  // --- 5) Scoring (0–100), combine rewards and penalties and clamp ---

  // Passphrase Length (0–30): 12 chars => ~10, 32 chars => ~30
  // Password Length (0–30): 12 chars => ~10, 32 chars => ~30
  // score += scale(totalLen, 12, 32, 0, 30);

  // Password & Passphrase length scoring
  // Derive a “reasonable” lower bound from your passphrase policy
  const reqMinWords = isPassword ? 1 : cfg.ppMinAcceptedWords; // pw must be 1, e.g. passPhrase 4
  const reqMinWordLen = isPassword
    ? cfg.pwMinAcceptedWordLength
    : cfg.ppMinAcceptedWordLength; // e.g., 4
  const lowerBaseline = reqMinWords * reqMinWordLen + (reqMinWords - 1); // includes spaces, e.g. 19, works for pw to set baseline at required min length
  const upperBaseline = lowerBaseline + 16; // or +20, +24 — how quickly you want to max length points, works for pw
  //   score += scale(totalLen, lowerBaseline, upperBaseline, 0, 30);

  // NEW: make password length worth more and give baseline credit at the minimum
  const lengthOutMin =
    isPassword && totalLen >= cfg.pwMinAcceptedWordLength
      ? 20
      : !isPassword && totalLen >= 19
      ? 30
      : 0; // 16 chars gets baseline 20 pts
  const lengthOutMax = isPassword ? 40 : 50; // passwords can earn up to 40 from length
  score += scale(
    totalLen,
    lowerBaseline,
    upperBaseline,
    lengthOutMin,
    lengthOutMax
  );

  // Passphrase only scoring
  // Word count (0–25): 2 => 0, 3 => 8, 4 => 12, 5 => 18, 6-7 => 22, 8+ => 25
  if (!isPassword) {
    if (wordCount >= 8) score += 25;
    else if (wordCount >= 6) score += 22;
    else if (wordCount === 5) score += 18;
    else if (wordCount === 4) score += 16; //added 4
    else if (wordCount === 3) score += 8;
    else score += 0;

    if (wordCount >= cfg.ppMinAcceptedWords) {
      messages.policiesMet.push(`🟢 Acceptable word count.`);
    } else {
      messages.errors.push(`🔴 Word count not met.`);
    }

    // Passphrase only scoring for length of individual words
    // Word length quality (−10 to +15): reward long words, penalize very short
    let wordLenScore = 0;
    for (const L of wordLens) {
      if (L >= 8) wordLenScore += 4;
      else if (L >= 6) wordLenScore += 3;
      else if (L >= 4) wordLenScore += 2;
      else if (L <= 3) wordLenScore -= 2;

      if (L < cfg.ppMinAcceptedWordLength) {
        complete = false;
        messages.errors.push(
          `🔴 Some words do not meet minimum length of ${cfg.ppMinAcceptedWordLength}.`
        );
      }
    }
    wordLenScore = Math.max(-10, Math.min(15, wordLenScore));
    score += wordLenScore;
  }

  // Password & Passphrase
  // Class (lower, upper, digits, symbols) diversity (0–16)
  //   score += classCount * 4;
  // NEW: heavier weight for passwords
  score += isPassword ? classCount * 6 : classCount * 4;

  // pw policy met bonus
  //   if (pwPolicyMet) score += 20; // baseline award for meeting 16+ w/ all 4 classes

  // Extra: symbol variety (0–8)
  const usedSymbols = new Set(
    normalized.match(new RegExp(`[${symbolClass}]`, "g")) || []
  );
  score += Math.min(8, usedSymbols.size * 2);

  // Charset penalty
  if (hasInvalidSymbol) score -= 5;

  // Pattern penalties
  if (details.signals.commonPhrase) score -= 15;
  if (details.signals.commonWordRatio >= 0.5) score -= 10;
  if (details.signals.sequentialNumbers) score -= 8;
  if (details.signals.keyboardSeq) score -= 8;
  if (details.signals.repeatedBlocks) score -= 8;
  if (details.signals.calendarWords) score -= 5;

  // Clamp
  score = Math.max(0, Math.min(MAX_SCORE, Math.round(score)));
  //   score = Math.max(0, Math.min(score, MAX_SCORE));
  const percent = Math.round((score / MAX_SCORE) * 100);

  // Decide strength
  let strength = "Weak";
  let color = "danger";
  if (score > cfg.moderateMax) {
    strength = "Strong";
    color = "success";
  } else if (score > cfg.weakMax) {
    strength = "Moderate";
    color = "warning";
  }

  //determine if requirements met for either
  //   if (
  //     isPassword &&
  //     totalLen >= 16 &&
  //     hasLower &&
  //     hasUpper &&
  //     hasDigit &&
  //     hasAllowedSymbol
  //   ) {
  //     complete = true;
  //   } else {
  //     if (wordCount >= cfg.ppMinAcceptedWords && !hasInvalidSymbol && hasLower)
  //       complete = true;
  //   }
  if (isPassword) {
    complete = metCount >= cfg.pwPolicyRequiredCount;
  } else {
    // existing passphrase completion check remains
    if (wordCount >= cfg.ppMinAcceptedWords && !hasInvalidSymbol && hasLower)
      complete = true;
  }

  return finish(strength, color, percent, messages, isPassword, complete);

  // --- Helpers ---
  //   return { level, color, percent, messages, complete };

  function finish(strength, color, percent, messages, isPassword, complete) {
    // if (extraNeg) d.hints.push(extraNeg);
    return { strength, color, percent, messages, isPassword, complete };
    // return { ok, strength, score, details: d };
  }
  /**
   *
   * @param {number} x       current value
   * @param {number} inMin   minimum for this value
   * @param {number} inMax   maximum for this value
   * @param {number} outMin  return minimum if value is <= minimum
   * @param {number} outMax  return maximum if value is >= maximum
   * @returns {number}
   *
   * Example: scale(totalLen, 12, 32, 0, 30)
   * Example: scale(totalLen, 16, 32, 0, 30) PW
   */
  function scale(x, inMin, inMax, outMin, outMax) {
    if (x <= inMin) return outMin; // 1-12
    if (x >= inMax) return outMax; // 30
    const t = (x - inMin) / (inMax - inMin); // (14-12)/(32-12) = 2/20 = 0.1
    return outMin + t * (outMax - outMin); // 0 + 0.1 * (30 - 0) = 0.1*30 = 3
  }
  function escapeForCharClass(s) {
    return s.replace(/[-\\^$*+?.()|[\]{}]/g, "\\$&");
  }
  function isSequential(strDigits, descending = false) {
    for (let i = 1; i < strDigits.length; i++) {
      const prev = Number(strDigits[i - 1]),
        cur = Number(strDigits[i]);
      if (descending) {
        if (cur !== prev - 1) return false;
      } else {
        if (cur !== prev + 1) return false;
      }
    }
    return true;
  }
  function hasRepeatedWordBlock(arr) {
    // Detect ABAB or ABCABC patterns (simple check)
    const n = arr.length;
    for (let block = 1; block <= Math.floor(n / 2); block++) {
      if (n % block !== 0) continue;
      let ok = true;
      for (let i = block; i < n; i++) {
        if (arr[i] !== arr[i - block]) {
          ok = false;
          break;
        }
      }
      if (ok) return true;
    }
    return false;
  }
  function commonPrefixLen(a, b) {
    let i = 0;
    while (i < a.length && i < b.length && a[i] === b[i]) i++;
    return i;
  }
  function commonSuffixLen(a, b) {
    let i = 0;
    while (
      i < a.length &&
      i < b.length &&
      a[a.length - 1 - i] === b[b.length - 1 - i]
    )
      i++;
    return i;
  }
  function longestCommonSubstringLen(a, b) {
    // DP O(n*m), fine for short words
    const n = a.length,
      m = b.length;
    let maxLen = 0;
    const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
    for (let i = 1; i <= n; i++) {
      for (let j = 1; j <= m; j++) {
        if (a[i - 1] === b[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1] + 1;
          if (dp[i][j] > maxLen) maxLen = dp[i][j];
        }
      }
    }
    return maxLen;
  }
}
